import { Button, Text } from '@cretesuite/design-system'
import { IonPage, IonRouterOutlet, IonSpinner } from '@ionic/react'
import { IonReactRouter } from '@ionic/react-router'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { RouteProps } from 'react-router-dom'
import {
  Redirect,
  Route,
  matchPath,
  useHistory,
  useLocation,
  useParams,
} from 'react-router-dom'
import {
  type ScopedClientTrace,
  ScopedGraphQLProvider,
  createScopedGraphQLClient,
} from '../context/ScopedGraphQLProvider'
import { ShortLinkProvider } from '../context/ShortLinkContext'
import { GetShortLink } from '../graphql/shortLinks'
import {
  decodePathname,
  internalPathForRedirect,
  isExpired,
} from '../helpers/shortLinkRedirect'
import { CurrentUserProvider, useCurrentUser } from '../hooks/useCurrentUser'
import { useSession } from '../hooks/useSession'
import { useTraceRequest } from '../hooks/useTraceRequest'
import { StandardLayout } from '../layouts/StandardLayout'
import { assignmentDetailRoutes } from '../routers/assignmentDetailRoutes'

type Resolved = { path: string; token: string | null; sub: number | null }
/** `invalid`: the link is unknown, expired or off-app. `network`: the resolve
 * itself failed (offline, 5xx), so the link may well be fine. */
type ResolveError = 'invalid' | 'network'
type ResolveState =
  | { kind: 'resolving' }
  | { kind: 'error'; reason: ResolveError }
  | { kind: 'ready'; resolved: Resolved }

/** Resolve the slug on an unauthenticated client — the query is public and
 * must not ride whatever session (or none) the visitor has. */
const useResolveShortLink = (
  slug: string | undefined,
  trace: ScopedClientTrace,
): { state: ResolveState; retry: () => void } => {
  const [state, setState] = useState<ResolveState>({ kind: 'resolving' })
  const [attempt, setAttempt] = useState(0)
  const retry = useCallback(() => {
    setState({ kind: 'resolving' })
    setAttempt((n) => n + 1)
  }, [])
  useEffect(() => {
    if (!slug) {
      setState({ kind: 'error', reason: 'invalid' })
      return
    }
    let cancelled = false
    const client = createScopedGraphQLClient(null, trace)
    client
      .query(GetShortLink, { slug }, { requestPolicy: 'network-only' })
      .toPromise()
      .then((result) => {
        if (cancelled) return
        // urql resolves (never rejects) on failures, and resolver or field-level
        // errors still carry partial data: any error means the link could not be
        // judged, so it is treated as transient, not a dead link
        if (result.error) {
          console.error('[ShortLink] resolve failed', result.error)
          setState({ kind: 'error', reason: 'network' })
          return
        }
        const link = result.data?.shortLink
        const path = link ? internalPathForRedirect(link.redirect) : null
        const isUsableLink =
          link != null && path != null && !isExpired(link.expires)
        if (!isUsableLink) {
          setState({ kind: 'error', reason: 'invalid' })
          return
        }
        setState({
          kind: 'ready',
          resolved: {
            path,
            token: link.authorization?.token ?? null,
            sub: link.sub ?? null,
          },
        })
      })
      .catch((error) => {
        console.error('[ShortLink] resolve failed', error)
        if (!cancelled) setState({ kind: 'error', reason: 'network' })
      })
    return () => {
      cancelled = true
    }
  }, [slug, trace, attempt])
  return { state, retry }
}

/**
 * Short-link mini-app (parity with v1's ShortUrl controller).
 * Route: /o/:slug — assignment SMS links arrive as crew.cretesuite.com/<slug>.
 *
 * Resolves the public `shortLink` query, then runs the target screens on the
 * operator token the link carries, so a LOGGED-OUT operator opens their
 * assignment (and order/ack from there — photos need the main session) without
 * signing in. If the visitor is already logged in as that same operator we
 * exit the mini-app and navigate on the real session instead.
 */
export const ShortUrlPage = () => {
  const { slug } = useParams<{ slug: string }>()
  const { loggedIn } = useSession()
  const { user, loading: userLoading } = useCurrentUser()
  // One trace for the whole journey: the resolve and the mini-app's requests
  // share a session id, so a "link didn't work" report can be correlated
  const trace = useTraceRequest()
  const { state, retry } = useResolveShortLink(slug, trace)
  // v1's ExitShortUrlApp nuance: the nested router owns the URL bar while
  // mounted, so exit is two steps — unmount the mini-app first (this state
  // flips the render), THEN redirect on the root router.
  const [exitTarget, setExitTarget] = useState<string | null>(null)

  if (exitTarget !== null) {
    return <Redirect to={exitTarget} />
  }

  if (state.kind === 'error') {
    return state.reason === 'network' ? (
      <ShortUrlRetryScreen onRetry={retry} />
    ) : (
      <ShortUrlInvalidLinkScreen />
    )
  }
  if (state.kind === 'resolving') {
    return <ShortUrlLoadingScreen />
  }
  // First load only: `loading` flips on every `me` refetch, and unmounting the
  // mini-app then would drop its router, history and in-flight mutations
  const isIdentityPending = loggedIn && userLoading && !user
  // Mounting the mini-app now and swapping to the real session a beat later
  // would thrash routers/clients (v1's ControlInitialRedirect guards the same race)
  if (isIdentityPending) {
    return <ShortUrlLoadingScreen />
  }

  const { path, token, sub } = state.resolved

  // Already signed in as this exact operator → leave the mini-app and use the
  // real session (main router + main client), like v1's ControlInitialRedirect.
  const isSameOperator =
    loggedIn && sub != null && user?.id != null && user.id === sub
  // Navigate on the main session (main router + client) instead of the mini-app
  const shouldUseMainSession = isSameOperator || token == null
  if (shouldUseMainSession) {
    return <Redirect to={path} />
  }

  return (
    <ShortUrlMiniApp
      slug={slug}
      token={token}
      trace={trace}
      initialPath={path}
      exitToApp={setExitTarget}
    />
  )
}

const isHandledBy = (routes: JSX.Element[], pathname: string) =>
  routes.some((route) => {
    const { path, exact } = route.props as RouteProps
    return matchPath(pathname, { path, exact }) !== null
  })

/**
 * Keeps the nested router on the mini-app's own screens (v1's
 * ControlInitialRedirect + ExitShortUrlApp.Listener, in one place):
 * - '/' is the browser sitting at /o/:slug itself: move to the link target
 *   BEFORE the outlet mounts;
 * - any other path the mini-app does not own leaves for the root router.
 * Deciding here, not with a pathless catch-all Route inside the outlet: a
 * pathless Route renders on every location when IonRouterOutlet has no stack
 * manager (see the /:404 workaround in AuthRouter).
 */
export const MiniAppRouter = ({
  initialPath,
  exitToApp,
  routes,
}: {
  initialPath: string
  exitToApp: (target: string) => void
  routes: JSX.Element[]
}) => {
  const history = useHistory()
  const location = useLocation()
  const isAtEntry = location.pathname === '/'
  const isHandled = isAtEntry || isHandledBy(routes, location.pathname)
  const exitTarget = `${location.pathname}${location.search}${location.hash}`
  useEffect(() => {
    if (isAtEntry) {
      history.replace(initialPath)
      return
    }
    if (!isHandled) exitToApp(exitTarget)
  }, [isAtEntry, isHandled, history, initialPath, exitToApp, exitTarget])
  // Keep an ion-page on screen for the frame before the redirect lands
  if (isAtEntry || !isHandled) return <ShortUrlLoadingScreen />
  return <IonRouterOutlet>{routes}</IonRouterOutlet>
}

/**
 * The target screens, scoped to the embedded token in their own router so all
 * in-app navigation (order, ack) stays under /o/:slug on that token.
 */
const ShortUrlMiniApp = ({
  slug,
  token,
  trace,
  initialPath,
  exitToApp,
}: {
  slug: string
  token: string
  trace: ScopedClientTrace
  initialPath: string
  exitToApp: (target: string) => void
}) => {
  // Photo screens are left out on purpose: upload needs the main session
  const routes = useMemo(() => assignmentDetailRoutes(), [])
  const shortLinkValue = useMemo(
    () => ({
      // Decoded like history's location.pathname, so entry compares equal
      entryPathname: decodePathname(initialPath.split(/[?#]/)[0]),
      exitToApp,
    }),
    [initialPath, exitToApp],
  )
  return (
    <ScopedGraphQLProvider token={token} trace={trace}>
      <ShortLinkProvider value={shortLinkValue}>
        <CurrentUserProvider>
          {/* IonReactRouter never unlistens its history, so each mount leaks a
              popstate listener: the identity gate above keeps this to one
              mount per link */}
          <IonReactRouter basename={`/o/${slug}`}>
            <MiniAppRouter
              initialPath={initialPath}
              exitToApp={exitToApp}
              routes={routes}
            />
          </IonReactRouter>
        </CurrentUserProvider>
      </ShortLinkProvider>
    </ScopedGraphQLProvider>
  )
}

const ShortUrlLoadingScreen = () => {
  const { _ } = useLingui()
  return (
    <IonPage>
      <StandardLayout title={_(msg`Loading...`)}>
        <div className='ion-padding text-center'>
          <IonSpinner name='crescent' />
        </div>
      </StandardLayout>
    </IonPage>
  )
}

const ShortUrlMessageScreen = ({
  title,
  message,
  action,
}: {
  title: string
  message: string
  action: JSX.Element
}) => (
  <IonPage>
    <StandardLayout title={title}>
      <div className='ion-padding text-center'>
        <Text variant='body' className='text-muted-foreground'>
          {message}
        </Text>
        <div className='ion-padding'>{action}</div>
      </div>
    </StandardLayout>
  </IonPage>
)

/** The resolve failed, the link may well be fine: v1 offers a retry here. An
 * error that repeats on every attempt still needs a way out, hence the second action. */
const ShortUrlRetryScreen = ({ onRetry }: { onRetry: () => void }) => {
  const { _ } = useLingui()
  const history = useHistory()
  return (
    <ShortUrlMessageScreen
      title={_(msg`An error occurred`)}
      message={_(msg`Sorry an error occurred, please refresh and try again`)}
      action={
        <>
          <Button onClick={onRetry}>{_(msg`Retry`)}</Button>
          <div className='ion-padding-top'>
            <Button
              variant='secondary'
              onClick={() => history.replace('/assignments')}
            >
              {_(msg`Go to my Assignments`)}
            </Button>
          </div>
        </>
      }
    />
  )
}

const ShortUrlInvalidLinkScreen = () => {
  const { _ } = useLingui()
  const history = useHistory()
  return (
    <ShortUrlMessageScreen
      title={_(msg`Link not available`)}
      message={_(
        msg`Sorry, that link is not valid or has expired. Please contact us if you need assistance.`,
      )}
      action={
        <Button onClick={() => history.replace('/assignments')}>
          {_(msg`Go to my Assignments`)}
        </Button>
      }
    />
  )
}

/** The app-level route, keyed by slug so a second SMS link remounts the whole
 * journey (resolve state and the nested router) instead of reusing the old one. */
export const shortUrlRoute = () => (
  <Route
    key='short-url'
    path='/o/:slug'
    render={({ match }) => <ShortUrlPage key={match.params.slug} />}
  />
)

export default ShortUrlPage
